/**
 * Central mapper: converts the V2 API flat response into the display model the
 * pot's summary card, split preview and approval panels consume.
 *
 * The breakdown ledger is deliberately not a consumer — it reads the flat
 * `allocations[]` and `deductions[]` itself (`ledger/toPotLedgerRows.ts`),
 * which is why the old site → plot → labour item → allocation rebuild is gone.
 */

import type { PotDetailsV2Response } from '@billda/api';
import type {
  PotDetailsDisplayModel,
  Deduction,
  FixedRateWorker,
  GangMemberSplit,
  PotRejectionReason,
} from '../types/potDetails.types';

export function mapPotDetailsV2(
  v2: PotDetailsV2Response
): PotDetailsDisplayModel {
  return {
    id: v2.id,
    isPrimary: v2.isPrimary,
    potPeriodId: v2.potPeriodId,
    owner: {
      ownerType: v2.type === 'Gang' ? 'Gang' : 'Solo',
      ownerName:
        v2.type === 'Gang' ? (v2.gang?.name ?? '') : (v2.solo?.ownerName ?? ''),
      gangId: v2.gang?.id ?? null,
    },
    period: {
      year: v2.period.year,
      weekNumber: v2.period.weekNumber,
      start: v2.period.start,
      end: v2.period.end,
      timezone: v2.period.timezone,
    },
    deadlines: v2.deadlines
      ? {
          bookInBy: v2.deadlines.bookInBy,
          verifyBy: v2.deadlines.verifyBy,
          submitBy: v2.deadlines.submitBy,
        }
      : null,
    autoSubmit: {
      isScheduled: v2.autoSubmit.isScheduled,
      scheduledAt: v2.autoSubmit.scheduledAt,
      isRecurringEnabled: v2.autoSubmit.isRecurringEnabled,
    },
    financials: v2.financials
      ? {
          grossAmount: v2.financials.gross,
          totalDeductions: v2.financials.totalDeductions,
          netAmount: v2.financials.net,
        }
      : null,
    approvals: {
      status: v2.status,
      submittedAt: v2.approvalTimeline.submittedAt,
      submittedByName: v2.approvalTimeline.submittedByName,
      approvedAt: v2.approvalTimeline.approvedAt,
      approvedByName: v2.approvalTimeline.approvedByName,
      rejectedAt: v2.approvalTimeline.rejectedAt,
      rejectedByName: v2.approvalTimeline.rejectedByName,
      hasOutstandingRejections: v2.rejectionManagement.hasOutstandingRejections,
    },
    deductions: v2.deductions.map(mapDeduction),
    fixedRateWorkers: mapFixedRateWorkers(v2.splitPreview.fixedRateWorkers),
    gangMemberSplits: mapGangMemberSplits(v2.splitPreview.percentageMembers),
    rejectionReasons: mapRejectionReasons(v2.rejectionManagement.reasons),
  };
}

// --- Individual record mappers ---

function mapDeduction(
  v2: PotDetailsV2Response['deductions'][number]
): Deduction {
  return {
    id: v2.id,
    description: v2.description,
    amount: v2.amount ?? 0,
    status: v2.status,
    siteId: v2.siteId,
    siteName: v2.siteName,
    plotId: v2.plotId,
    plotReference: v2.plotReference,
    createdAt: v2.createdAt,
    uniqueReference: v2.uniqueReference,
    canCancel: v2.canCancel,
  };
}

function mapFixedRateWorkers(
  workers: import('../types/potDetailsV2.types').SplitPreviewFixedRateWorkerDto[]
): FixedRateWorker[] {
  return workers.map(w => ({
    userId: w.userId,
    userName: w.name,
    rateValue: w.rateValue ?? 0,
    unitsWorked: w.unitsWorked ?? 0,
    bonusAmount: w.bonusAmount ?? 0,
    totalAmount: w.calculatedAmount,
    isGangMember: !w.isAdHoc,
    isFormerMember: w.isFormerMember,
  }));
}

function mapGangMemberSplits(
  members: import('../types/potDetailsV2.types').SplitPreviewMemberDto[]
): GangMemberSplit[] {
  return members.map(m => ({
    userId: m.userId,
    userName: m.name,
    amount: m.payout,
    percentage: m.splitPercentage ?? 0,
    isLeader: m.isLeader,
    isFormerMember: m.isFormerMember,
    isNewMember: m.isNewMember,
  }));
}

function mapRejectionReasons(
  reasons: import('../types/potDetailsV2.types').PotV2RejectionReasonDto[]
): PotRejectionReason[] {
  return reasons.map(r => ({
    id: r.id,
    reason: r.reason,
    createdAt: r.createdAt,
    createdByName: r.createdByName,
    isResolved: r.isResolved,
    resolvedAt: r.resolvedAt,
    resolvedByName: r.resolvedByName,
  }));
}
